import React from 'react';
import styled from '@emotion/styled';
import ViewHeader from './ViewHeader';
import { Camera, Device, deviceTitle, isUnpaired } from '../models/device';

export interface DevicesViewProps {
  devices: Device[];
  // Ids of devices with an in-flight ping (drives the spinner / disabled state).
  pinging: Record<string, boolean>;
  onPing: (device: Device) => void;
}

const STATUS_COLOR: Record<string, string> = {
  ONLINE: '#24b272',
  OFFLINE: '#e8412c',
  PENDING: '#aab0b5',
};

const DEVICE_BADGE: Record<string, { label: string; color: string; bg: string; border: string }> = {
  ONLINE: { label: 'Device online', color: '#1f9e63', bg: 'rgba(36,178,114,0.12)', border: 'rgba(36,178,114,0.3)' },
  OFFLINE: { label: 'Device offline', color: '#cf3320', bg: 'rgba(232,65,44,0.1)', border: 'rgba(232,65,44,0.3)' },
  PENDING: { label: 'Awaiting ping', color: '#6e757c', bg: 'rgba(110,117,124,0.1)', border: 'rgba(110,117,124,0.25)' },
};

function cameraEmptyLabel(status: Camera['status']): string {
  if (status === 'OFFLINE') return 'No signal';
  if (status === 'PENDING') return 'Awaiting feed';
  return 'No image yet';
}

function deviceMeta(device: Device): string {
  const base = device.lastPingLabel;
  if (isUnpaired(device)) {
    return `${base} · not associated with a location`;
  }
  const n = device.cameras.length;
  return `${base} · ${n} ${n === 1 ? 'camera' : 'cameras'}`;
}

// --- a single camera still / empty state -----------------------------------
const CameraTile = ({ camera }: { camera: Camera }) => {
  const dot = STATUS_COLOR[camera.status];
  const statusLabel = camera.status.charAt(0) + camera.status.slice(1).toLowerCase();
  return (
    <StyledCameraTile>
      <div className="frame">
        {camera.imageUrl ? (
          <>
            <img src={camera.imageUrl} alt="" />
            <div className="scrim" />
          </>
        ) : (
          <div className="empty">
            <span className="cam-off" aria-hidden="true">
              <span className="body" />
              <span className="lens" />
              <span className="hood" />
              <span className="slash" />
            </span>
            <span className="empty-label">{cameraEmptyLabel(camera.status)}</span>
          </div>
        )}
        <span className="status-pill">
          <span className="dot" style={{ background: dot, boxShadow: `0 0 0 3px ${dot}4d` }} />
          {statusLabel}
        </span>
        <span className="name">{camera.name}</span>
      </div>
    </StyledCameraTile>
  );
};

export default ({ devices, pinging, onPing }: DevicesViewProps) => {
  const online = devices.filter((d) => d.status === 'ONLINE' && !pinging[d.id]).length;
  const offline = devices.filter((d) => d.status === 'OFFLINE' && !pinging[d.id]).length;
  const pending = devices.filter((d) => d.status === 'PENDING' || pinging[d.id]).length;
  const totalCameras = devices.reduce((sum, d) => sum + d.cameras.length, 0);

  return (
    <StyledContainer>
      <div className="headerRow">
        <ViewHeader>Devices</ViewHeader>
        <div className="counts">
          <span className="count">
            <span className="cdot" style={{ background: STATUS_COLOR.ONLINE }} />
            <span className="num">{online}</span>
            <span className="clabel">Online</span>
          </span>
          <span className="count">
            <span className="cdot" style={{ background: STATUS_COLOR.OFFLINE }} />
            <span className="num">{offline}</span>
            <span className="clabel">Offline</span>
          </span>
          <span className="count">
            <span className="cdot" style={{ background: STATUS_COLOR.PENDING }} />
            <span className="num">{pending}</span>
            <span className="clabel">Pending</span>
          </span>
        </div>
      </div>
      <p className="summary">{totalCameras} cameras across the fleet</p>

      {devices.map((device) => {
        const isPinging = !!pinging[device.id];
        const badge = DEVICE_BADGE[device.status];
        const dotColor = STATUS_COLOR[device.status];
        return (
          <div className="card" key={device.id}>
            <div className="cardHeader">
              <span
                className={`headDot${isPinging ? ' pinging' : ''}`}
                style={isPinging ? undefined : { background: dotColor, boxShadow: `0 0 0 4px ${dotColor}29` }}
              />
              <h3>{deviceTitle(device)}</h3>
              {isPinging ? (
                <span className="badge" style={{ color: '#6e757c', background: 'rgba(110,117,124,0.1)', borderColor: 'rgba(110,117,124,0.25)' }}>
                  Pinging…
                </span>
              ) : (
                <span className="badge" style={{ color: badge.color, background: badge.bg, borderColor: badge.border }}>
                  {badge.label}
                </span>
              )}
              <span className="meta">{isPinging ? 'checking connection…' : deviceMeta(device)}</span>
              <div className="actions">
                {isPinging ? (
                  <span className="pingBtn disabled">
                    <span className="spin">↻</span>Pinging…
                  </span>
                ) : (
                  <button className="pingBtn" onClick={() => onPing(device)}>
                    <span className="reset">↻</span>Re-ping
                  </button>
                )}
              </div>
            </div>

            {/* Unpaired devices have no cameras section at all. */}
            {!isUnpaired(device) && device.cameras.length > 0 && (
              <div className="cameras">
                {device.cameras.map((camera) => (
                  <CameraTile key={camera.id} camera={camera} />
                ))}
              </div>
            )}

            {!isUnpaired(device) && device.cameras.length === 0 && (
              <div className="noCameras">
                <span className="cam-outline" aria-hidden="true">
                  <span className="body" />
                  <span className="lens" />
                  <span className="hood" />
                </span>
                <div>
                  <p className="title">No cameras configured</p>
                  <p className="sub">Connect a camera to this location’s device to start monitoring its stations.</p>
                </div>
              </div>
            )}
          </div>
        );
      })}
    </StyledContainer>
  );
};

const StyledCameraTile = styled.div`
  border: 1px solid rgba(110, 117, 124, 0.18);
  border-radius: 10px;
  overflow: hidden;
  background: #15171a;

  .frame {
    position: relative;
    aspect-ratio: 16 / 9;
    background: #15171a;
  }

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
  }

  .scrim {
    position: absolute;
    inset: 0;
    background: linear-gradient(to bottom, transparent 55%, rgba(0, 0, 0, 0.55));
  }

  .empty {
    position: absolute;
    inset: 0;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 9px;
    background: #1b1e22;
    background-image: radial-gradient(circle at 50% 42%, rgba(255, 255, 255, 0.04), transparent 60%);
  }

  .empty-label {
    color: #7b828a;
    font-size: 12px;
    font-weight: 600;
    letter-spacing: 0.03em;
  }

  /* camera-with-slash glyph for the empty state */
  .cam-off {
    position: relative;
    width: 38px;
    height: 27px;
    border: 2px solid #4b5158;
    border-radius: 5px;
    display: flex;
    align-items: center;
    justify-content: center;
  }
  .cam-off .lens {
    width: 12px;
    height: 12px;
    border: 2px solid #4b5158;
    border-radius: 50%;
  }
  .cam-off .hood {
    position: absolute;
    top: -7px;
    left: 9px;
    width: 11px;
    height: 7px;
    border: 2px solid #4b5158;
    border-bottom: none;
    border-radius: 3px 3px 0 0;
    background: #1b1e22;
  }
  .cam-off .slash {
    position: absolute;
    left: -6px;
    right: -6px;
    top: 50%;
    height: 2px;
    background: #6b7178;
    transform: rotate(-34deg);
  }

  .status-pill {
    position: absolute;
    top: 9px;
    left: 9px;
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 3px 8px 3px 7px;
    border-radius: 999px;
    background: rgba(0, 0, 0, 0.5);
    backdrop-filter: blur(2px);
    color: #fff;
    font-size: 10px;
    font-weight: 700;
    letter-spacing: 0.06em;
    text-transform: uppercase;
  }
  .status-pill .dot {
    width: 8px;
    height: 8px;
    border-radius: 50%;
  }

  .name {
    position: absolute;
    bottom: 9px;
    left: 11px;
    color: #fff;
    font-size: 13px;
    font-weight: 600;
    text-shadow: 0 1px 3px rgba(0, 0, 0, 0.7);
  }
`;

export const StyledContainer = styled.div`
  padding: 0 60px 60px;
  min-height: 100%;
  font-family: Helvetica, sans-serif;
  color: #6e757c;

  @keyframes gspin {
    to {
      transform: rotate(360deg);
    }
  }
  @keyframes gpulse {
    0%,
    100% {
      opacity: 1;
    }
    50% {
      opacity: 0.3;
    }
  }

  .headerRow {
    display: flex;
    align-items: center;
    gap: 24px;
    margin: 28px 0 4px;
  }

  h2 {
    margin: 0;
  }

  .counts {
    margin-left: auto;
    display: flex;
    gap: 28px;
  }
  .count {
    display: inline-flex;
    align-items: center;
    gap: 9px;
  }
  .cdot {
    width: 10px;
    height: 10px;
    border-radius: 50%;
  }
  .count .num {
    font-size: 22px;
    font-weight: 700;
    color: #1a1a1a;
    line-height: 1;
  }
  .count .clabel {
    font-size: 12px;
    color: #6e757c;
  }

  .summary {
    margin: 0 0 26px;
    font-size: 13px;
    color: #aab0b5;
  }

  .card {
    background: #fff;
    border: 1px solid rgba(110, 117, 124, 0.2);
    border-radius: 14px;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06);
    margin-bottom: 20px;
    overflow: hidden;
  }

  .cardHeader {
    display: flex;
    align-items: center;
    gap: 14px;
    padding: 17px 22px;
    border-bottom: 1px solid rgba(110, 117, 124, 0.12);
  }

  .headDot {
    width: 13px;
    height: 13px;
    border-radius: 50%;
    flex-shrink: 0;
  }
  .headDot.pinging {
    background: #aab0b5;
    animation: gpulse 1s ease-in-out infinite;
  }

  .cardHeader h3 {
    font-size: 17px;
    font-weight: 700;
    color: #1a1a1a;
    margin: 0;
    flex-shrink: 0;
    white-space: nowrap;
  }

  .badge {
    display: inline-flex;
    align-items: center;
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 0.04em;
    text-transform: uppercase;
    white-space: nowrap;
    padding: 4px 9px;
    border-radius: 999px;
    border: 1px solid;
    flex-shrink: 0;
  }

  .meta {
    font-size: 13px;
    color: #aab0b5;
    flex: 1;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .actions {
    margin-left: auto;
    flex-shrink: 0;
  }

  .pingBtn {
    display: inline-flex;
    align-items: center;
    gap: 7px;
    height: 36px;
    padding: 0 16px;
    background: #fff;
    border: 1px solid #325882;
    border-radius: 8px;
    color: #325882;
    font-size: 14px;
    font-weight: 600;
    cursor: pointer;
    font-family: Helvetica, sans-serif;
    white-space: nowrap;
    transition: background 0.15s linear;
  }
  .pingBtn:hover {
    background: rgba(50, 88, 130, 0.07);
  }
  .pingBtn.disabled {
    border-color: rgba(110, 117, 124, 0.25);
    color: #aab0b5;
    cursor: default;
  }
  .pingBtn .reset {
    font-size: 17px;
    line-height: 1;
    margin-top: -1px;
  }
  .pingBtn .spin {
    display: inline-block;
    font-size: 17px;
    line-height: 1;
    animation: gspin 0.8s linear infinite;
  }

  .cameras {
    padding: 18px 22px;
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(260px, 1fr));
    gap: 16px;
  }

  .noCameras {
    padding: 38px 22px;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 13px;
    text-align: center;
  }
  .noCameras .title {
    margin: 0 0 4px;
    font-size: 15px;
    font-weight: 700;
    color: #1a1a1a;
  }
  .noCameras .sub {
    margin: 0 auto;
    font-size: 13px;
    color: #aab0b5;
    max-width: 330px;
    line-height: 1.5;
  }
  .cam-outline {
    position: relative;
    width: 54px;
    height: 39px;
    border: 2px solid #c7ccd0;
    border-radius: 8px;
    display: flex;
    align-items: center;
    justify-content: center;
  }
  .cam-outline .lens {
    width: 18px;
    height: 18px;
    border: 2px solid #c7ccd0;
    border-radius: 50%;
  }
  .cam-outline .hood {
    position: absolute;
    top: -8px;
    left: 14px;
    width: 15px;
    height: 8px;
    border: 2px solid #c7ccd0;
    border-bottom: none;
    border-radius: 4px 4px 0 0;
    background: #fff;
  }
`;
